import { createContext, useState, useEffect, useMemo, useCallback, ReactNode, useContext } from 'react';

import { saveEncrypted, loadEncrypted } from '../../utils/encryptedStorage';
import useAuth from '../../hooks/useAuth';
import { Discount, DiscountType } from './discount.type';

const STORAGE_KEY = 'checkout_discount';
const STORAGE_KEY_FINISHED = 'checkout_discount_finished';
const PLUGINSPACE_STORAGE_KEY = 'pluginspace_context_guard';
const PLUGINSPACE_CHANGE_EVENT = 'pluginspace:change';

// ============ TYPES ============

interface DiscountContextType {
  // Estado
  discounts: Discount[];
  finishedDiscount: DiscountState | null;
  productDiscount: Discount | null;
  couponDiscount: Discount | null;
  freightDiscount: Discount | null;
  paymentDiscount: Discount | null;
  generalDiscount: Discount | null;
  cashbackDiscount: Discount | null;

  // Valores computados
  totalDiscountValue: number;
  totalDiscountPercent: number;

  // Ações
  applyDiscount: (discount: Discount) => void;
  removeDiscount: (type: DiscountType) => void;
  removeGeneralDiscount: (type: DiscountType) => void;
  removePaymentDiscount: (type: DiscountType) => void;
  clearAllDiscounts: () => void;

  finishDiscounts: () => void;

  applyCoupon: (code: string, value: number, description?: string) => void;
  removeCoupon: () => void;

  applyFreightDiscount: (value: number, description?: string) => void;
  removeFreightDiscount: () => void;

  applyCashback: (value: number, description?: string) => void;
  removeCashback: () => void;

  // Queries
  hasDiscount: (type?: DiscountType) => boolean;
  getDiscountValue: (type?: DiscountType) => number;
  calculateDiscountedValue: (originalValue: number) => number;
}

interface DiscountProviderProps {
  children: ReactNode;
}

interface DiscountState {
  discounts: Discount[];
}

const DEFAULT_STATE: DiscountState = { discounts: [] };

// ============ CONTEXT ============

export const DiscountContext = createContext<DiscountContextType | undefined>(undefined);

// ============ PROVIDER ============

export function DiscountProvider({ children }: DiscountProviderProps) {
  const { currentPluginspaceId } = useAuth();
  const [state, setState] = useState<DiscountState>(() => {
    // Carregar do localStorage na inicialização
    const saved = loadEncrypted<DiscountState>(STORAGE_KEY);
    return saved || DEFAULT_STATE;
  });

  const [finishedDiscount, setFinishedDiscount] = useState<DiscountState | null>(() => {
    const saved = loadEncrypted<DiscountState>(STORAGE_KEY_FINISHED);
    return saved || null;
  });

  useEffect(() => {
    if (!currentPluginspaceId) return;
    const stored = loadEncrypted<{ pluginspaceId: number }>(PLUGINSPACE_STORAGE_KEY);
    const storedPluginspaceId = stored?.pluginspaceId ? String(stored.pluginspaceId) : null;
    const currentId = String(currentPluginspaceId);

    if (storedPluginspaceId && storedPluginspaceId !== currentId) {
      setState(DEFAULT_STATE);
    }

    if (storedPluginspaceId !== currentId) {
      saveEncrypted(PLUGINSPACE_STORAGE_KEY, { pluginspaceId: currentPluginspaceId });
    }
  }, [currentPluginspaceId]);

  // ============ PERSISTÊNCIA ============

  useEffect(() => {
    // Salvar no localStorage sempre que o estado mudar
    saveEncrypted(STORAGE_KEY, state);
  }, [state]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handlePluginspaceChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ pluginspaceId: number }>;
      const newPluginspaceId = customEvent.detail?.pluginspaceId;
      if (!newPluginspaceId) return;

      const stored = loadEncrypted<{ pluginspaceId: number }>(PLUGINSPACE_STORAGE_KEY);
      const storedPluginspaceId = stored?.pluginspaceId ? String(stored.pluginspaceId) : null;
      const nextId = String(newPluginspaceId);

      if (storedPluginspaceId && storedPluginspaceId !== nextId) {
        setState(DEFAULT_STATE);
      }

      if (storedPluginspaceId !== nextId) {
        saveEncrypted(PLUGINSPACE_STORAGE_KEY, { pluginspaceId: newPluginspaceId });
      }
    };

    window.addEventListener(PLUGINSPACE_CHANGE_EVENT, handlePluginspaceChange as EventListener);
    return () => window.removeEventListener(PLUGINSPACE_CHANGE_EVENT, handlePluginspaceChange as EventListener);
  }, []);

  // ============ VALORES COMPUTADOS (MEMOIZADOS) ============

  const productDiscount = useMemo(() => {
    return state.discounts.find(d => d.type === 'percent' || d.type === 'fixed') || null;
  }, [state.discounts]);

  const couponDiscount = useMemo(() => {
    return state.discounts.find(d => d.type === 'coupon') || null;
  }, [state.discounts]);

  const freightDiscount = useMemo(() => {
    return state.discounts.find(d => d.type === 'freight') || null;
  }, [state.discounts]);

  const totalDiscountValue = useMemo(() => {
    return state.discounts
      .filter(d => d.type === 'fixed' || d.type === 'coupon' || d.type === 'freight' || d.type === 'cashback')
      .reduce((sum, d) => sum + d.value, 0);
  }, [state.discounts]);

  const paymentDiscount = useMemo(() => {
    return state.discounts.find(d => d.type === 'payment') || null;
  }, [state.discounts]);

  const generalDiscount = useMemo(() => {
    return state.discounts.find(d => d.type === 'general') || null;
  }, [state.discounts]);

  const cashbackDiscount = useMemo(() => {
    return state.discounts.find(d => d.type === 'cashback') || null;
  }, [state.discounts]);

  const totalDiscountPercent = useMemo(() => {
    return state.discounts
      .filter(d => d.type === 'percent')
      .reduce((sum, d) => sum + d.value, 0);
  }, [state.discounts]);

  // ============ AÇÕES (MEMOIZADAS) ============

  const applyDiscount = useCallback((discount: Discount) => {
    setState(prev => {
      // Remover desconto existente do mesmo tipo
      const filtered = prev.discounts.filter(d => d.type !== discount.type);
      return {
        ...prev,
        discounts: [...filtered, { ...discount, appliedAt: new Date() }],
      };
    });
  }, []);

  const removeDiscount = useCallback((type: DiscountType) => {
    setState(prev => ({
      ...prev,
      discounts: prev.discounts.filter(d => d.type !== type),
    }));
  }, []);

  const clearAllDiscounts = useCallback(() => {
    setState({ discounts: [] });
  }, []);

  const applyCoupon = useCallback((code: string, value: number, description?: string) => {
    const coupon: Discount = {
      type: 'coupon',
      value,
      code,
      description: description || `Cupom: ${code}`,
      appliedAt: new Date(),
    };
    applyDiscount(coupon);
  }, [applyDiscount]);

  const removeCoupon = useCallback(() => {
    removeDiscount('coupon');
  }, [removeDiscount]);

  const applyFreightDiscount = useCallback((value: number, description?: string) => {
    const freight: Discount = {
      type: 'freight',
      value,
      description: description || 'Desconto no frete',
      appliedAt: new Date(),
    };
    applyDiscount(freight);
  }, [applyDiscount]);

  const removeFreightDiscount = useCallback(() => {
    removeDiscount('freight');
  }, [removeDiscount]);

  const applyCashback = useCallback((value: number, description?: string) => {
    const cashback: Discount = {
      type: 'cashback',
      value,
      description: description || 'Resgate de cashback',
      appliedAt: new Date(),
    };
    applyDiscount(cashback);
  }, [applyDiscount]);

  const removeCashback = useCallback(() => {
    removeDiscount('cashback');
  }, [removeDiscount]);

  const removePaymentDiscount = useCallback(() => {
    removeDiscount('payment');
  }, [removeDiscount]);

  const removeGeneralDiscount = useCallback(() => {
    removeDiscount('general');
  }, [removeDiscount]);

  const finishDiscounts = useCallback(() => {
    setFinishedDiscount(state);
    saveEncrypted(STORAGE_KEY_FINISHED, state);
  }, [state]);

  // ============ QUERIES (MEMOIZADAS) ============

  const hasDiscount = useCallback((type?: DiscountType) => {
    if (type) {
      return state.discounts.some(d => d.type === type);
    }
    return state.discounts.length > 0;
  }, [state.discounts]);

  const getDiscountValue = useCallback((type?: DiscountType) => {
    if (type) {
      const discount = state.discounts.find(d => d.type === type);
      return discount?.value || 0;
    }
    return totalDiscountValue;
  }, [state.discounts, totalDiscountValue]);

  const calculateDiscountedValue = useCallback((originalValue: number) => {
    let finalValue = originalValue;

    // Aplicar desconto percentual
    if (totalDiscountPercent > 0) {
      finalValue -= (originalValue * totalDiscountPercent) / 100;
    }

    // Aplicar desconto fixo
    finalValue -= totalDiscountValue;

    // Garantir que não seja negativo
    return Math.max(0, finalValue);
  }, [totalDiscountValue, totalDiscountPercent]);

  // ============ VALUE (MEMOIZADO) ============

  const value = useMemo<DiscountContextType>(() => ({
    // Estado
    discounts: state.discounts,
    finishedDiscount: finishedDiscount,
    productDiscount,
    couponDiscount,
    freightDiscount,
    paymentDiscount,
    generalDiscount,
    cashbackDiscount,
    // Valores computados
    totalDiscountValue,
    totalDiscountPercent,

    // Ações
    applyDiscount,
    removeDiscount,
    clearAllDiscounts,
    applyCoupon,
    removeCoupon,
    applyFreightDiscount,
    removeFreightDiscount,
    applyCashback,
    removeCashback,
    removeGeneralDiscount,
    removePaymentDiscount,
    finishDiscounts,

    // Queries
    hasDiscount,
    getDiscountValue,
    calculateDiscountedValue,

  }), [
    state.discounts,
    productDiscount,
    couponDiscount,
    freightDiscount,
    totalDiscountValue,
    totalDiscountPercent,
    paymentDiscount,
    generalDiscount,
    cashbackDiscount,
    applyDiscount,
    removeDiscount,
    clearAllDiscounts,
    applyCoupon,
    removeCoupon,
    applyFreightDiscount,
    removeFreightDiscount,
    applyCashback,
    removeCashback,
    hasDiscount,
    getDiscountValue,
    calculateDiscountedValue,
    removeGeneralDiscount,
    removePaymentDiscount,
  ]);

  return <DiscountContext.Provider value={value}>{children}</DiscountContext.Provider>;
}


/**
 * Hook principal - retorna todo o contexto
 */
export function useDiscount() {
  const context = useContext(DiscountContext);
  if (!context) {
    throw new Error('useDiscount must be used within DiscountProvider');
  }
  return context;
}

/**
 * Hook seletor - apenas lista de descontos
 */
export function useDiscounts() {
  const { discounts } = useDiscount();
  return useMemo(() => discounts, [discounts]);
}

/**
 * Hook seletor - apenas desconto de produto
 */
export function useProductDiscount() {
  const { productDiscount } = useDiscount();
  return useMemo(() => productDiscount, [productDiscount]);
}

/**
 * Hook seletor - apenas cupom
 */
export function useCouponDiscount() {
  const { couponDiscount } = useDiscount();
  return useMemo(() => couponDiscount, [couponDiscount]);
}

/**
 * Hook seletor - apenas desconto de frete
 */
export function useFreightDiscount() {
  const { freightDiscount } = useDiscount();
  return useMemo(() => freightDiscount, [freightDiscount]);
}

/**
 * Hook seletor - apenas valor total de desconto
 */
export function useDiscountValue() {
  const { totalDiscountValue } = useDiscount();
  return useMemo(() => totalDiscountValue, [totalDiscountValue]);
}

/**
 * Hook seletor - apenas ações
 * NUNCA re-renderiza
 */
export function useDiscountActions() {
  const {
    applyDiscount,
    removeDiscount,
    clearAllDiscounts,
    applyCoupon,
    removeCoupon,
    applyFreightDiscount,
    removeFreightDiscount,
  } = useDiscount();

  return useMemo(() => ({
    applyDiscount,
    removeDiscount,
    clearAllDiscounts,
    applyCoupon,
    removeCoupon,
    applyFreightDiscount,
    removeFreightDiscount,
  }), [
    applyDiscount,
    removeDiscount,
    clearAllDiscounts,
    applyCoupon,
    removeCoupon,
    applyFreightDiscount,
    removeFreightDiscount,
  ]);
}

/**
 * Hook seletor - queries
 */
export function useDiscountQueries() {
  const { hasDiscount, getDiscountValue, calculateDiscountedValue } = useDiscount();

  return useMemo(() => ({
    hasDiscount,
    getDiscountValue,
    calculateDiscountedValue,
  }), [hasDiscount, getDiscountValue, calculateDiscountedValue]);
}

